import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { MAX_RETENTION_DAYS, RETENTION_PRESETS, callsApi, type CallSettings } from '../lib/calls';
import { useI18n } from '../lib/i18n';
import { Button, Card, CardHeader, ConfirmDialog, ErrorState, Field, Input, LoadingBlock, useToast } from './ui';

/**
 * Settings › Calls (2026-10-29): how long recordings are kept — forever until an
 * administrator says otherwise — and how long a call rings, how many people one
 * holds and how long it may last. A shorter retention deletes the audio of the
 * recordings already older than it: the page says how many first, and saves
 * only once that is confirmed. The calls themselves are never deleted.
 */
type Choice = 'forever' | '30' | '180' | '365' | 'custom';

const choiceOf = (days: number | null): Choice =>
  days === null ? 'forever' : (RETENTION_PRESETS as readonly number[]).includes(days) ? (String(days) as Choice) : 'custom';

export function CallsSettingsPanel() {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['calls', 'settings'], queryFn: callsApi.settings });
  const [choice, setChoice] = useState<Choice>('forever');
  const [custom, setCustom] = useState('');
  const [ring, setRing] = useState('');
  const [size, setSize] = useState('');
  const [length, setLength] = useState('');
  const [confirming, setConfirming] = useState<{ affected: number; days: number } | null>(null);

  useEffect(() => {
    if (!data) return;
    setChoice(choiceOf(data.recordingRetentionDays));
    setCustom(data.recordingRetentionDays !== null && choiceOf(data.recordingRetentionDays) === 'custom' ? String(data.recordingRetentionDays) : '');
    setRing(String(data.ringTimeoutSeconds));
    setSize(String(data.maxParticipants));
    setLength(String(data.maxDurationMinutes));
  }, [data]);

  const days: number | null | 'invalid' = (() => {
    if (choice === 'forever') return null;
    if (choice !== 'custom') return Number(choice);
    const n = Number(custom);
    return Number.isInteger(n) && n >= 1 && n <= MAX_RETENTION_DAYS ? n : 'invalid';
  })();

  const values = (): CallSettings => ({
    recordingRetentionDays: days === 'invalid' ? null : days,
    ringTimeoutSeconds: Number(ring),
    maxParticipants: Number(size),
    maxDurationMinutes: Number(length),
  });

  const save = useMutation({
    mutationFn: (confirmDeletion: boolean) => callsApi.saveSettings({ ...values(), confirmDeletion }),
    onSuccess: () => {
      setConfirming(null);
      toast.success(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'CONFIRMATION_REQUIRED') {
        const details = e.details as { affected?: number; days?: number } | undefined;
        setConfirming({ affected: details?.affected ?? 0, days: details?.days ?? 0 });
        return;
      }
      setConfirming(null);
      toast.error(e.message);
    },
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const changed =
    days !== data.recordingRetentionDays ||
    Number(ring) !== data.ringTimeoutSeconds ||
    Number(size) !== data.maxParticipants ||
    Number(length) !== data.maxDurationMinutes;
  const options: Array<{ id: Choice; label: string }> = [
    { id: 'forever', label: t('calls.retention.forever') },
    { id: '30', label: t('calls.retention.days', { n: 30 }) },
    { id: '180', label: t('calls.retention.days', { n: 180 }) },
    { id: '365', label: t('calls.retention.days', { n: 365 }) },
    { id: 'custom', label: t('calls.retention.custom') },
  ];

  return (
    <>
      <Card>
        <CardHeader title={t('calls.retention.title')} subtitle={t('calls.retention.subtitle')} />
        <fieldset>
          <legend className="sr-only">{t('calls.retention.title')}</legend>
          <div className="grid gap-2 sm:grid-cols-5">
            {options.map((o) => (
              <label
                key={o.id}
                className={
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-body-sm ' +
                  (choice === o.id ? 'border-primary bg-primary/10 text-[var(--text-primary)]' : 'hairline text-[var(--text-secondary)]')
                }
              >
                <input type="radio" name="calls-retention" value={o.id} checked={choice === o.id} onChange={() => setChoice(o.id)} />
                {o.label}
              </label>
            ))}
          </div>
        </fieldset>
        {choice === 'custom' && (
          <Field
            className="mt-4 max-w-xs"
            label={t('calls.retention.customDays')}
            hint={t('calls.retention.customHint', { max: MAX_RETENTION_DAYS })}
            error={custom !== '' && days === 'invalid' ? t('calls.retention.invalid') : null}
          >
            <Input type="number" inputMode="numeric" min={1} max={MAX_RETENTION_DAYS} step={1} value={custom} onChange={(e) => setCustom(e.target.value)} />
          </Field>
        )}
        <p className="mt-4 rounded-md bg-[var(--surface-sunken)] p-3 text-body-sm text-[var(--text-secondary)]">{t('calls.retention.explain')}</p>
      </Card>

      <Card>
        <CardHeader title={t('calls.settings.callsTitle')} subtitle={t('calls.settings.callsSubtitle')} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('calls.settings.ring')} hint={t('calls.settings.ringHint')}>
            <Input type="number" min={15} max={120} step={1} value={ring} onChange={(e) => setRing(e.target.value)} />
          </Field>
          <Field label={t('calls.settings.size')} hint={t('calls.settings.sizeHint')}>
            <Input type="number" min={2} max={16} step={1} value={size} onChange={(e) => setSize(e.target.value)} />
          </Field>
          <Field label={t('calls.settings.length')} hint={t('calls.settings.lengthHint')}>
            <Input type="number" min={10} max={720} step={1} value={length} onChange={(e) => setLength(e.target.value)} />
          </Field>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" loading={save.isPending && !confirming} disabled={!changed || days === 'invalid'} onClick={() => save.mutate(false)}>
          <Save className="h-4 w-4" aria-hidden />
          {t('common.save')}
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          title={t('calls.retention.confirmTitle')}
          confirmLabel={t('calls.retention.confirmButton', { n: confirming.affected })}
          danger
          busy={save.isPending}
          onConfirm={() => save.mutate(true)}
          onCancel={() => setConfirming(null)}
        >
          <p className="text-body-sm text-[var(--text-primary)]">
            {t('calls.retention.confirmBody', { n: confirming.affected, days: confirming.days })}
          </p>
          <p className="mt-2 text-body-sm text-muted">{t('calls.retention.confirmKeep')}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
